/**
 * Daily hours of multi-day events (table event_day_hours, migration 2026-10-27-1120). Display + storage only.
 * Reads: any signed-in user who can see the event. Writes: set_event_day_hours (replaces the whole set), allowed to
 * the event's creator, an editor of the event, or an admin -- the same people who may change its dates.
 */
import { supabase } from "./supabase";
import type { DayHours } from "../utils/eventDayHours";

export interface EventDayHoursRow extends DayHours {
  event_id: string;
}

// 60 event ids keep the GET URL short. Rows are read in pages of PAGE (= PostgREST max_rows on PROD, 1000) until a
// short page, ordered by the unique (event_id, service_date), so nothing is silently truncated even when an event
// keeps rows for dates it no longer covers (external audit 705a4bd9, hardening note: those are not bounded by 14).
const CHUNK = 60;
const PAGE = 1000;

type RawRow = EventDayHoursRow & {
  events?: { all_day: boolean | null; date_tbd: boolean | null } | null;
};

/** The two builder calls readPaged needs (kept structural: the generated builder types recurse too deeply). */
interface Pageable {
  order: (column: string, opts: { ascending: boolean }) => Pageable;
  range: (
    from: number,
    to: number,
  ) => PromiseLike<{ data: unknown; error: unknown }>;
}

async function readPaged(build: () => unknown): Promise<RawRow[]> {
  const out: RawRow[] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await (build() as Pageable)
      .order("event_id", { ascending: true })
      .order("service_date", { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) {
      throw error;
    }
    const rows = (data ?? []) as unknown as RawRow[];
    out.push(...rows);
    if (rows.length < PAGE) {
      return out;
    }
  }
}

const toDayHours = (r: RawRow): DayHours => ({
  service_date: r.service_date,
  opens_at: r.opens_at,
  closes_at: r.closes_at,
});

/**
 * DISPLAY read: hours rows for these events, grouped by event id. Events without rows are absent from the map.
 * Rows of an event that is now all-day or date TBD are left out (they do not apply; the display would ignore them).
 */
export async function getEventDayHours(
  eventIds: ReadonlyArray<string>,
): Promise<Map<string, DayHours[]>> {
  const ids = Array.from(new Set(eventIds.filter(Boolean)));
  const out = new Map<string, DayHours[]>();
  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK);
    const rows = await readPaged(() =>
      supabase
        .from("event_day_hours")
        .select(
          "event_id, service_date, opens_at, closes_at, events!inner(all_day, date_tbd)",
        )
        .in("event_id", slice),
    );
    for (const r of rows) {
      if (r.events?.all_day || r.events?.date_tbd) {
        continue;
      }
      const list = out.get(r.event_id) ?? [];
      list.push(toDayHours(r));
      out.set(r.event_id, list);
    }
  }
  return out;
}

/**
 * EDITOR read: every row saved for one event, whatever the event's current dates or flags ([] = none saved).
 * Throws on failure, so callers can tell "none saved" from "could not read" (external audit 705a4bd9 F1).
 */
export async function getEventDayHoursForEvent(
  eventId: string,
): Promise<DayHours[]> {
  const rows = await readPaged(() =>
    supabase
      .from("event_day_hours")
      .select("event_id, service_date, opens_at, closes_at")
      .eq("event_id", eventId),
  );
  return rows.map(toDayHours);
}

/** Replace an event's daily hours ([] clears them). Throws the server's error (code 42501 / 22023 / 23514). */
export async function setEventDayHours(
  eventId: string,
  days: DayHours[],
): Promise<DayHours[]> {
  const { data, error } = await supabase.rpc("set_event_day_hours", {
    p_event_id: eventId,
    p_days: days.map((d) => ({
      service_date: d.service_date,
      opens_at: d.opens_at,
      closes_at: d.closes_at,
    })),
  });
  if (error) {
    throw error;
  }
  return ((data ?? []) as EventDayHoursRow[]).map((r) => ({
    service_date: r.service_date,
    opens_at: r.opens_at,
    closes_at: r.closes_at,
  }));
}

/** May the signed-in user change this event's daily hours? */
export async function canEditEventDayHours(eventId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("event_day_hours_can_edit", {
    p_event_id: eventId,
  });
  if (error) {
    throw error;
  }
  return data === true;
}

/** Plain-language message for a failed save. */
export function describeDayHoursError(err: unknown): string {
  const e = err as { code?: string; message?: string } | null;
  const msg = e?.message ?? "";
  if (e?.code === "42501" || msg.includes("EVENT_DAY_HOURS_NOT_ALLOWED")) {
    return "Daily hours can be changed by the event's owner, an editor of the event, or an admin.";
  }
  if (msg.includes("EVENT_DAY_HOURS_DATE_OUTSIDE_EVENT")) {
    return "The event's dates changed while you were editing. Reload the event and check the daily hours.";
  }
  if (
    msg.includes("EVENT_DAY_HOURS_NOT_APPLICABLE") ||
    msg.includes("EVENT_DAY_HOURS_SPAN")
  ) {
    return "Daily hours only apply to timed events that run over 2 to 14 days.";
  }
  if (msg.includes("closes_after_opens")) {
    return "Each day's closing time must be after its opening time.";
  }
  return msg || "Unknown error";
}
